/* global FB, fb */
// FB is set in views/facebook/_async_init through the fb sdk

// this will be converted to local jquery in the webpack sitewide-to-local loader
import $ from 'sitewide-jquery';

interface GraphAPIError {
  message: string;
  type?: string;
  code?: number;
  error_subcode?: number;
  error_user_msg: string;
  error_user_title?: string;
  fbtrace_id: string;
}

interface GraphAPIErrorResponse {
  error?: GraphAPIError;
}

type PermissionStatus = {
  permission: string;
  status: 'granted' | 'declined' | 'expired';
};

interface PermissionResponse extends GraphAPIErrorResponse {
  data: Array<PermissionStatus>;
}

function jQuerify<T>(promise: Promise<T>) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const deferred = $.Deferred<T, any, never>();
  promise
    .then((res) => deferred.resolve(res))
    .catch((reason) => deferred.reject(reason));
  return deferred;
}

/*
Facebook (and google.ts) is used in several undeclared dependencies to manage the
Facebook OAuth process (FB). It relies on _async_init.html.erb to set up a script tag that imports
the FB SDK JS with a callback set to create a singleton of this class as the global Facebook object.

See these files for usage (may be incomplete):
app/assets/javascripts/auth/oauth_adapter.coffee
app/assets/javascripts/auth/oauth_buttons.coffee
app/assets/javascripts/facebook_contacts.coffee
app/views/users/_connected_apps.html.slim
*/
export class FacebookSingleton {
  readonly scopes = [
    'email',
    'user_friends',
    'public_profile',
    'user_link',
  ].join(',');

  protected isReady = false;
  private readyListeners: Array<() => void> = [];

  protected connectedUrl = '/auth/connect';
  protected disconnectedUrl = '/auth/disconnect';

  runReadyListeners() {
    this.isReady = true;
    let listener: (() => void) | undefined;
    while ((listener = this.readyListeners.shift())) {
      listener();
    }
  }

  private queueForExecution(fn: () => void) {
    if (this.isReady) {
      fn();
    }
    this.readyListeners.push(fn);
  }

  // Log visitor (maybe logged-in to Academia.edu, maybe not) into Facebook
  // and request they grant permissions to Academia.edu
  // If the reRequest flag is given. then also re-request declined permissions
  authorize(reRequest = false) {
    return new Promise<fb.AuthResponse>((resolve, reject) => {
      const params: fb.LoginOptions = {
        scope: this.scopes,
        return_scopes: true,
      };
      if (reRequest) {
        params.auth_type = 'rerequest';
      }
      this.queueForExecution(() => {
        FB.login((response: fb.StatusResponse) => {
          this.loginPromiseResolver(response, resolve, reject);
        }, params);
      });
    });
  }

  // Wraps the Promise from this.authorize in a $.Deferred for backwards compatibility
  $authorize(reRequest = false) {
    return jQuerify(this.authorize(reRequest));
  }

  // Check if visitor (maybe logged-in to Academia.edu, maybe not)
  // is logged in to Facebook
  getLoginStatus(cb: (response: fb.StatusResponse) => void) {
    this.queueForExecution(() => FB.getLoginStatus(cb));
  }

  // Check if visitor (maybe logged-in to Academia.edu, maybe not)
  // is logged in to Facebook, returning promise that they are
  checkLoginStatus() {
    return new Promise<fb.AuthResponse>((resolve, reject) => {
      this.getLoginStatus((response: fb.StatusResponse) => {
        this.loginPromiseResolver(response, resolve, reject);
      });
    });
  }

  // Wraps the Promise from this.checkLoginStatus in a $.Deferred for backwards compatibility
  $check_login_status() {
    return jQuerify(this.checkLoginStatus());
  }

  // Will resolve to true if all the permissions have been granted, false otherwise
  checkPermissionsGranted(permissions: string[]) {
    return new Promise<boolean>((resolve) => {
      FB.api<PermissionResponse>('/me/permissions', (resp) => {
        if (resp && !resp.error) {
          const permissionsAccepted = permissions.every((perm) => {
            const granted = resp.data.find((permStatus) => {
              return (
                permStatus.permission === perm &&
                permStatus.status === 'granted'
              );
            });
            return granted != null;
          });
          resolve(permissionsAccepted);
        } else {
          resolve(false);
        }
      });
    });
  }

  // Wraps the Promise from this.checkPermissionsGranted in a $.Deferred for backwards
  // compatibility
  $check_permissions_granted(permissions: string[]) {
    return jQuerify(this.checkPermissionsGranted(permissions));
  }

  // Connect logged-in Academia.edu user's Facebook account
  // to their Academia.edu account.
  $register_connected(authResponse: fb.AuthResponse) {
    return $.post(this.connectedUrl, {
      provider: 'facebook',
      uid: authResponse.userID,
      'oauth[token]': authResponse.accessToken,
      'oauth[expires]': authResponse.expiresIn,
      'oauth[scopes]': authResponse.grantedScopes,
    });
  }

  deregister_connected_and_reload() {
    FB.login((response: fb.StatusResponse) => {
      if (response.authResponse) {
        FB.api('/me/permissions', 'delete', {}, () => {
          $.post(this.disconnectedUrl, { provider: 'facebook' }, () => {
            window.location.reload();
          });
        });
      }
    });
  }

  private loginPromiseResolver(
    response: fb.StatusResponse,
    resolve: (resp: fb.AuthResponse) => void,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    reject: (reason?: any) => void,
  ): void {
    if (response.authResponse) {
      FB.api<PermissionResponse>('/me/permissions', (resp) => {
        if (resp && !resp.error) {
          const granted = resp.data.find((permStatus) => {
            return (
              permStatus.permission === 'email' &&
              permStatus.status === 'granted'
            );
          });
          if (granted) {
            resolve(response.authResponse);
            return;
          }
        }
        // Make sure the user needs to auth again if they
        // dont give access to email
        FB.api('/me/permissions', 'delete', {}, () => {});
        reject(resp.error);
      });
    } else {
      reject();
    }
  }
}

const Facebook = new FacebookSingleton();

/* eslint-disable @typescript-eslint/no-explicit-any */
(window as any).Facebook = Facebook;

// academiaAuthReadyFacebook is set in facebook/_async_init.html.erb, if that hasn't run yet, this
// will return and _async_init will set it up when it runs
if ((window as any).academiaAuthReadyFacebook) {
  Facebook.runReadyListeners();
  delete (window as any).academiaAuthReadyFacebook;
} else {
  (window as any).InitFacebook = Facebook.runReadyListeners.bind(Facebook);
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export default Facebook;
